import type { Step as RcStep } from '@sb/remote-control/types';

import { type ValidationRule, type RoutineView } from '../../types';
import { ActionRequiredError, type WarningFinding } from '../../util/errors';
import {
  validateCodeExpression,
  validateConditional,
  validateStoredConditionShape,
} from '../../util/validators';

import { CanonicalStepRuleBase } from './base';
import type { CanonicalValidateArgs } from './base';

class WaitRule extends CanonicalStepRuleBase {
  public constructor() {
    super('steps.Wait', 'Wait');
  }

  protected validate({ args, routine, warn }: CanonicalValidateArgs): void {
    type WArgs = Extract<RcStep.Arguments, { argumentKind: 'Wait' }>;
    const a = args as WArgs;
    warn(this.checkCondition(a, routine));
    warn(this.checkTimeExpression(a));
  }

  /**
   * Validate the wait condition against the routine.
   * Throws ActionRequiredError if invalid.
   *
   * @throws {ActionRequiredError}
   */
  private checkCondition(
    args: Extract<RcStep.Arguments, { argumentKind: 'Wait' }>,
    routine: RoutineView,
  ): WarningFinding | undefined {
    if (!args.condition) return undefined;
    const raw = args.condition;

    const parsed = typeof raw === 'string' ? JSON.parse(raw || '[]') : raw;

    // Only the stored form is JSON text; a runtime-format condition object is
    // not a stored condition.
    if (typeof raw === 'string') validateStoredConditionShape(parsed);

    // `validateConditional` expects Conditional[][] – a non-array value
    // (e.g. a single object) would silently bypass the for-loops inside.
    // Guard against that by treating non-array values as empty.
    const conditions = Array.isArray(parsed) ? parsed : [];

    return validateConditional(conditions, routine);
  }

  /**
   * When `timeInputKind === 'expression'`, the `expression` field must be set
   * and contain a valid code expression.
   *
   * @throws {ActionRequiredError}
   */
  private checkTimeExpression(
    args: Extract<RcStep.Arguments, { argumentKind: 'Wait' }>,
  ): WarningFinding | undefined {
    if (args.timeInputKind !== 'expression') return undefined;

    if (!args.expression) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Time expression is required',
        fieldId: 'expression',
      });
    }

    return validateCodeExpression(
      args.expression,
      'expression',
      'Time expression',
    );
  }
}

export const WaitValidation: ValidationRule = new WaitRule().toValidationRule();
